import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Banner, BlockStack, Box, Button, Card, InlineStack, Link, Modal, Spinner, Text, TextField, Thumbnail } from '@shopify/polaris';
import { ImageIcon } from '@shopify/polaris-icons';
import { cacApi } from './apiClient';
import { CertaintyPill, StatusBadge } from './CertaintyPill';
import CategoryPicker from './CategoryPicker';
import EvidenceCard from './EvidenceCard';
import AiCard from './AiCard';
import FacetRow from './FacetRow';
import { groupIds, leafName } from './facetUtils';
import { reviewPath } from './routes';

// What Shopify holds today for a facet: the vendor for brand, else the display of the metafield the facet maps to
// (facet_meta.key like "shopify.color-pattern", or the dictionary entry's namespace/key), else an "existing:" evidence value.
// A list shows its whole content, e.g. "Black, White".
export function currentValue(facet, product, evidence, metafields, meta, fm) {
  if (facet === 'brand') return product.vendor || '';
  const m = (metafields || []).find((x) => (fm && fm.key && `${x.namespace}.${x.key}` === fm.key)
    || (meta && meta.kind === 'standard' && x.namespace === meta.namespace && x.key === meta.key));
  if (m && m.display) {
    try { const v = JSON.parse(m.display); return Array.isArray(v) ? v.join(', ') : String(v); } catch (e) { return String(m.display); }
  }
  const e = (evidence || []).find((x) => x.facet === facet && String(x.source).startsWith('existing:'));
  return e ? String(e.value) : '';
}

export default function ProductPage({ id, dictionary, notify, reloadDictionary }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const from = params.get('from') || '';
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState('');
  const [pickingCategory, setPickingCategory] = useState(false);
  const [recomputed, setRecomputed] = useState(false);   // a category change just succeeded: true, or { note } when the recompute was skipped
  const [others, setOthers] = useState(null);
  const [showDescription, setShowDescription] = useState(false);   // after a category change: { old, oldName, newValue, newName, count, done } for "apply to all"
  const [newValue, setNewValue] = useState(null);   // { facet, label, text }

  const [liveRun, setLiveRun] = useState(null);   // id of the AI run being polled
  const [analyzed, setAnalyzed] = useState(false);
  const noteRef = useRef(notify);
  noteRef.current = notify;

  const seq = useRef(0);   // an older response never overwrites a newer one
  const load = useCallback(() => {
    const n = ++seq.current;
    return cacApi.product(id).then((x) => { if (n === seq.current) setD(x); }).catch((e) => { if (n === seq.current) setError(e.message); });
  }, [id]);
  useEffect(() => { setD(null); setError(''); setRecomputed(false); setLiveRun(null); setAnalyzed(false); load(); }, [load]);
  // Resume polling a run that was already going when the page opened.
  const resumeId = d && d.live_run ? d.live_run.id : null;
  useEffect(() => { if (resumeId) setLiveRun((cur) => cur || resumeId); }, [resumeId]);
  // Poll the live run every 3 s; when it stops, reload the product and say how it went.
  useEffect(() => {
    if (!liveRun) return undefined;
    let on = true;
    let timer;
    const tick = async () => {
      let r;
      try { r = await cacApi.run(liveRun); } catch (e) { r = null; }   // a missed poll is retried
      if (!on) return;
      if (!r || r.status === 'queued' || r.status === 'running') { timer = setTimeout(tick, 3000); return; }
      // The run is over: handled exactly once, and the spinner always clears.
      try {
        const fresh = await cacApi.product(id);
        if (!on) return;
        setD(fresh);
        if (r.status === 'completed') noteRef.current(`Analyzed · $${((r.cost_cents || 0) / 100).toFixed(2)}`);
        else noteRef.current(r.error || r.note || `Run ${r.status}`, true);
      } catch (e) {
        if (on) noteRef.current(`Could not reload the product: ${e.message}`, true);
      }
      if (on) { setLiveRun(null); setAnalyzed(true); }
    };
    timer = setTimeout(tick, 3000);
    return () => { on = false; clearTimeout(timer); };
  }, [liveRun, id]);
  // From the click until the answer the button stays off, so a double-click cannot queue two runs.
  const [queuing, setQueuing] = useState(false);
  const queuingRef = useRef(false);
  const analyze = async () => {
    if (queuingRef.current) return;
    queuingRef.current = true;
    setQueuing(true);
    try { const r = await cacApi.analyzeProduct(id); setLiveRun(r.run_id); }
    catch (e) { notify(e.message, true); }
    finally { queuingRef.current = false; setQueuing(false); }
  };

  const metaOf = useMemo(() => Object.fromEntries((dictionary || []).map((f) => [f.id, f])), [dictionary]);
  const labelOf = useCallback((f) => (metaOf[f] || {}).label || f, [metaOf]);

  const run = async (fn, okText) => {
    setBusy(true); setRefused('');
    let ok = null;
    try { const r = await fn(); notify(okText(r)); await load(); ok = { r }; }
    catch (e) { if (e.status === 409) setRefused(e.message); else notify(e.message, true); }
    setBusy(false);
    return ok;
  };
  const edit = (facet, value) => run(() => cacApi.editFacet(id, facet, value), () => `Edited ${labelOf(facet)}: ${Array.isArray(value) ? value.join(', ') || '(none)' : value}`);
  // A category change recomputes the facets on the server (no AI call); the response is the effective proposal only,
  // so the product is re-fetched and both groups re-render from it.
  const leafOf = (name) => String(name || '').split(' > ').pop();
  const changeCategory = async (h) => {
    setPickingCategory(false);
    const was = (d && d.category) || {};
    const old = was.proposed_id || was.current_id || null;
    const oldName = was.proposed_name || was.current_name || old;
    const ok = await run(() => cacApi.editFacet(id, 'category', `${h.id}|${h.name}`),
      (r) => (r && r.note ? 'Changed category' : 'Changed category — facets recomputed'));
    if (!ok) return;
    setRecomputed(ok.r && ok.r.note ? { note: ok.r.note } : true);
    setOthers(null);
    if (old && old !== h.id) {   // offer the same move to every other product still in the old category
      try {
        const r = await cacApi.categoryOthers(id, old);
        if (r.count > 0) setOthers({ old, oldName, newValue: `${h.id}|${h.name}`, newName: leafOf(h.name), count: r.count, done: false });
      } catch (e) { /* the offer is optional */ }
    }
  };
  const applyToAll = async () => {
    const o = others;
    if (!o) return;
    const ok = await run(() => cacApi.applyCategoryToAll(id, o.old, o.newValue, o.oldName),
      (r) => `Moved ${r.products} other product${r.products === 1 ? '' : 's'} to ${o.newName}`);
    if (ok) setOthers({ ...o, done: true, count: ok.r ? ok.r.products : o.count });
  };
  const saveNew = async () => {
    const { facet, text } = newValue;
    if (!text.trim()) return;
    setNewValue(null);
    await run(() => cacApi.editFacet(id, facet, text.trim()), () => `Added ${labelOf(facet)} value '${text.trim()}' as pending`);
    reloadDictionary();
  };

  if (error) return <Banner tone="critical"><p>{error}</p></Banner>;
  if (!d) return <InlineStack align="center"><Spinner accessibilityLabel="Loading product" /></InlineStack>;
  const { product, effective: eff, evidence } = d;
  const cat = eff.category || {};
  const groups = groupIds(d);
  const fmeta = d.facet_meta || {};
  const leaf = leafName(cat.proposed_name || cat.current_name);
  const rowFor = (k) => (
    <FacetRow key={k} id={k} meta={metaOf[k] || { id: k, label: k, values: [] }} fm={fmeta[k]} f={(eff.facets || {})[k] || {}} busy={busy}
      current={currentValue(k, product, evidence, d.metafields, metaOf[k], fmeta[k])}
      onEdit={edit} onAddNew={(facet, label) => setNewValue({ facet, label, text: '' })} />
  );

  return (
    <BlockStack gap="400">
      <InlineStack><Button variant="plain" onClick={() => navigate(reviewPath(from))}>← Back to review</Button></InlineStack>
      {refused && <Banner tone="critical" title={refused} onDismiss={() => setRefused('')}><p>Resolve the conflict or pending value on this product first.</p></Banner>}
      <Card>
        <InlineStack gap="400" wrap={false} blockAlign="start" align="space-between">
          <Thumbnail source={product.image_url || ImageIcon} alt="" size="large" />
          <BlockStack gap="200">
            <Text as="h2" variant="headingLg">{product.title}</Text>
            <Text as="p" tone="subdued">
              {product.vendor} · {product.product_type || '—'} · {product.total_inventory ?? 0} in stock ·{' '}
              <Link url={d.shopify_url} external>Open in Shopify</Link>
            </Text>
            <InlineStack gap="300" blockAlign="center">
              <Text as="span">Certainty</Text><CertaintyPill value={eff.certainty} />
              <Text as="span">Status</Text><StatusBadge status={eff.review_status} />
              {liveRun && <InlineStack gap="150" blockAlign="center"><Spinner size="small" accessibilityLabel="Analyzing" /><Text as="span" tone="subdued">Analyzing… image · vendor pages · category</Text></InlineStack>}
            </InlineStack>
          </BlockStack>
          <BlockStack gap="100" inlineAlign="end">
            <Button variant="primary" onClick={analyze} loading={queuing} disabled={queuing || !!liveRun || !!product.no_image}>{analyzed || d.ai ? 'Analyze again' : 'Analyze with AI'}</Button>
            <Box maxWidth="220px"><Text as="p" variant="bodySm" tone="subdued">Forces every layer, fresh: image, vendor page and category pick, even where the catalog is sure. Caches are ignored.</Text></Box>
            {product.no_image && <Text as="p" variant="bodySm" tone="subdued">No image on this product; it is never analyzed.</Text>}
          </BlockStack>
        </InlineStack>
      </Card>

      <Card>
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">Category</Text>
          <InlineStack gap="300" blockAlign="center" wrap>
            <Box borderWidth="025" borderColor="border" borderRadius="200" padding="200" minWidth="240px">
              <Text as="p" variant="bodyXs" tone="subdued">CURRENT (SHOPIFY)</Text>
              <Text as="p">{cat.current_name || '—'}</Text>
            </Box>
            <Text as="span" tone="subdued">→</Text>
            <Box borderWidth="025" borderColor="border-success" borderRadius="200" padding="200" minWidth="240px">
              <Text as="p" variant="bodyXs" tone="subdued">PROPOSED{cat.changed ? '' : ' (KEPT)'}</Text>
              <InlineStack gap="200"><Text as="p">{cat.proposed_name || '—'}</Text><CertaintyPill value={cat.certainty} /></InlineStack>
            </Box>
          </InlineStack>
          <InlineStack><Button disabled={busy} onClick={() => setPickingCategory(true)}>Choose another category…</Button></InlineStack>
          {others && !others.done && (
            <Banner tone="info" title={`${others.count} other product${others.count === 1 ? ' is' : 's are'} also in ${leafOf(others.oldName)}`}
              action={{ content: `Move all ${others.count} to ${others.newName}`, onAction: applyToAll, loading: busy }} onDismiss={() => setOthers(null)}>
              <p>Each gets the same change, ready for Review (nothing is written to Shopify until approved). From then on, every product found in {leafOf(others.oldName)} goes to {others.newName}.</p>
            </Banner>
          )}
          {others && others.done && (
            <Banner tone="success" onDismiss={() => setOthers(null)}>
              <p>Moved {others.count} other product{others.count === 1 ? '' : 's'} to {others.newName}. Every product found in {leafOf(others.oldName)} goes there from now on.</p>
            </Banner>
          )}
          <Text as="p" variant="bodySm" tone="subdued">{cat.reason || ''}{(cat.sources || []).length ? ` · ${cat.sources.join(', ')}` : ''}</Text>
        </BlockStack>
      </Card>

      {product.description && (
        <Card>
          <BlockStack gap="200">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingSm">Description (Shopify)</Text>
              <Button variant="plain" onClick={() => setShowDescription((v) => !v)}>{showDescription ? 'Hide' : 'Show'}</Button>
            </InlineStack>
            <Text as="p" variant="bodySm" tone="subdued">The vendor's text is read first: its LABEL: VALUE lines are evidence for the category's attributes, and the AI reads the rest.</Text>
            {showDescription && <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, fontSize: 13 }}>{product.description}</pre>}
          </BlockStack>
        </Card>
      )}

      <AiCard ai={d.ai} costCents={d.cost_cents} />

      {recomputed && recomputed.note && (
        <Banner tone="warning" onDismiss={() => setRecomputed(false)}><p>{recomputed.note}</p></Banner>
      )}
      {recomputed === true && (
        <Banner tone="info" onDismiss={() => setRecomputed(false)}>
          <p><strong>Facets recomputed</strong> from the stored evidence for {leaf} — no AI call. Values the evidence cannot settle are empty; Analyze with AI fills them for this category.</p>
        </Banner>
      )}

      <Card padding="0">
        <Box padding="400">
          <BlockStack gap="100">
            <Text as="h3" variant="headingMd">Category metafields (Shopify){leaf ? ` — ${leaf}` : ''}</Text>
            <Text as="p" variant="bodySm" tone="subdued">{groups.shopify.length} attribute{groups.shopify.length === 1 ? '' : 's'} of this category, written to Shopify's own fields (shopify.*). Edits are saved as you make them and teach the tool.</Text>
          </BlockStack>
        </Box>
        <div>{groups.shopify.map(rowFor)}</div>
      </Card>

      <Card padding="0">
        <Box padding="400">
          <BlockStack gap="100">
            <Text as="h3" variant="headingMd">Soccer Post metafields</Text>
            <Text as="p" variant="bodySm" tone="subdued">{groups.soccer_meta.length} of Soccer Post's own fields (soccer_meta.*). Grey badges are evidence: tag:, type, title, existing: a metafield already on the product. A warning-tone value is not one of Shopify's values: accept it in the Dictionary or drop it.</Text>
          </BlockStack>
        </Box>
        <div>{groups.soccer_meta.map(rowFor)}</div>
      </Card>

      <EvidenceCard evidence={evidence} facetLabel={labelOf} />

      <Card>
        <InlineStack align="space-between" blockAlign="center" wrap>
          <Text as="span" tone="subdued">Un-approve takes the product back out of the write queue.</Text>
          <InlineStack gap="200">
            <Button disabled={busy} onClick={() => run(() => cacApi.unapprove([Number(id)]), () => 'Un-approved')}>Un-approve</Button>
            <Button variant="primary" disabled={busy || eff.review_status === 'approved'} onClick={() => run(() => cacApi.approve([Number(id)]), () => 'Approved')}>Approve</Button>
          </InlineStack>
        </InlineStack>
      </Card>

      {pickingCategory && (
        <CategoryPicker onClose={() => setPickingCategory(false)}
          onPick={changeCategory} />
      )}
      {newValue && (
        <Modal open onClose={() => setNewValue(null)} title={`New ${newValue.label} value`}
          primaryAction={{ content: 'Save', onAction: saveNew, disabled: !newValue.text.trim() }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setNewValue(null) }]}>
          <Modal.Section>
            <BlockStack gap="200">
              <TextField label="Value" value={newValue.text} onChange={(t) => setNewValue({ ...newValue, text: t })} autoComplete="off" autoFocus />
              <Text as="p" tone="subdued">It is saved as pending in the Dictionary until you accept it there.</Text>
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}
    </BlockStack>
  );
}
